import {ApiClient} from '@hconnect/apiclient'
import {spawnErrorToast, spawnSuccessToast} from '@hm/fundamental'
import {useMutation, UseMutationOptions, useQueryClient} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useTranslation} from 'react-i18next'

import {TasksApiPath, FEATURE_NAMESPACE} from '../../../consts'
import {CreateMachineSentryMaintenanceNotificationActionDto} from '../../../types'

export const MachineSentryActionQueryKey = 'machine-sentry-action'

type Params = {
  plantUpmId?: string
  externalId: string
}

export const useCreateMachineSentryActionMaintenanceNotification = (
  {plantUpmId, externalId}: Params,
  options?: UseMutationOptions<
    string,
    AxiosError,
    CreateMachineSentryMaintenanceNotificationActionDto
  >
) => {
  const queryClient = useQueryClient()
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const path = `${TasksApiPath.MACHINE_SENTRY_ACTIONS}/${plantUpmId}/machine-sentry-actions/${externalId}/maintenance-notification`

  return useMutation<string, AxiosError, CreateMachineSentryMaintenanceNotificationActionDto>({
    ...options,
    mutationKey: [MachineSentryActionQueryKey, externalId, plantUpmId],
    mutationFn: async (data) => {
      const response = await ApiClient.axiosInstance.post<string>(path, {
        sourceEventId: externalId,
        ...data
      })
      return response.data
    },
    onError: (...args) => {
      spawnErrorToast(t('machineSentryActions.message.failedToCreateMaintenanceNotification'))
      options?.onError?.(...args)
    },
    onSuccess: async (...args) => {
      spawnSuccessToast(t('machineSentryActions.message.maintenanceNotificationCreated'))
      await queryClient.invalidateQueries({
        queryKey: [MachineSentryActionQueryKey],
        refetchType: 'all'
      })
      options?.onSuccess?.(...args)
    }
  })
}
